import { Alert, Flex, Skeleton } from 'antd'
import { ReactNode, useContext, useState } from 'react'
import { AuthenticationContext } from '../../../AuthenticationContext'
import { useLegacyJobDataQuery } from '../../../domain/jobs/jobsQueries'
import { useBudgets } from '../../Budgets/budgetQueries'
import { YearlyBudget } from '../../../domain/budget'
import { LegacyJobData, VisitReviewedState } from '../../../domain/jobs/jobs'
import ItemsRoot from '../../Items/ItemsRoot'
import JobInvoicing from '../JobInvoicing'
import VisitsPriceSummary from '../../visits/VisitsPriceSummary'
import VisitsTable from '../../visits/VisitsTable'
import EditVisitModal from '../../visits/EditVisitModal'
import LinkCustomerToQuickBooks from '../../quickbooks/LinkCustomerToQuickBooks'
import LinkCostCodeToQuickBooks from '../../quickbooks/LinkCostCodeToQuickBooks'
import ViewJobSummaryButton from '../../Items/ViewJobSummaryButton'
import ToggleCatalogBrowserButton from '../../ItemCatalog/ToggleCatalogBrowserButton'
import PrintItemsSummaryModal from '../../Items/ItemSummary/PrintItemsSummaryModal'

import { useIsSavingJobItems } from '../../Items/itemQueries'

type BodyParts = {
  jobData: LegacyJobData
  onLinkCustomer: () => void
  invoicing: ReactNode
  visits: ReactNode
}

type Props = {
  jobId: number
  googleApiKey: string
  spreadVisitToolbar?: boolean
  onPendingEditsChange?: (pending: boolean) => void
  children: (parts: BodyParts) => ReactNode
}

const JobInvoicingBody = ({ jobId, googleApiKey, onPendingEditsChange, spreadVisitToolbar = false, children }: Props) => {
  const { currentUser } = useContext(AuthenticationContext)
  const [hasPendingEdits, setHasPendingEdits] = useState(false)
  const isSaving = useIsSavingJobItems(jobId)
  const [selectedVisitId, setSelectedVisitId] = useState<string | null>(null)
  const [linkCustomerId, setLinkCustomerId] = useState<number | undefined>(undefined)
  const [linkCostCodeId, setLinkCostCodeId] = useState<number | undefined>(undefined)
  const [isPrintItemsSummaryModalOpen, setIsPrintItemsSummaryModalOpen] = useState(false)
  const jobQuery = useLegacyJobDataQuery(jobId)
  const budgetsQuery = useBudgets()

  if (jobQuery.isLoading) {
    return <div style={{ padding: '24px' }}>
      <Skeleton active />
    </div>
  }

  if (!jobQuery.data) {
    return <div style={{ padding: '24px' }}>
      <Alert message="Error" description="There was a problem loading the job." type="error" />
    </div>
  }

  const jobData = jobQuery.data
  const budgets = (budgetsQuery.data?.budgets ?? []).filter(x => x.year === jobData.budgetYear).map(x => x as YearlyBudget)

  return <ItemsRoot
    jobId={jobData.id}
    jobType={jobData.type}
    jobIsSold={jobData.sold}
    onChange={changes => {
      const pending = changes.addedItems.length + changes.updatedItems.length + changes.deletedItems.length > 0
      setHasPendingEdits(pending)
      onPendingEditsChange?.(pending)
    }}
    saveCounter={0}
    workareas={jobData.workareas}
    budgets={budgets}
    focusedView={'visits'}
    onTemplateAdd={() => {}}
    autoSave={true}
  >
    {children({
      jobData,
      onLinkCustomer: () => setLinkCustomerId(jobData.customer.id),
      invoicing: <JobInvoicing
        jobNeedsToBeSaved={hasPendingEdits || isSaving}
        customerId={jobData.customer.id}
        customerQboId={jobData.customer.qboId}
        onLinkCustomerToQbo={() => setLinkCustomerId(jobData.customer.id)}
        onLinkCostCodeToQbo={setLinkCostCodeId}
        costCodeRefreshCounter={0}
        onInvoicesChanged={() => {}}
      />,
      visits: [
        <VisitsPriceSummary key="summary" />,
        <VisitsTable
          key="not-reviewed"
          reviewedState={VisitReviewedState.NotReviewed}
          jobId={jobData.id}
          googleApiKey={googleApiKey}
          onEdit={setSelectedVisitId}
          toolbar={<Flex justify={spreadVisitToolbar ? 'space-between' : 'start'} align="center" gap={8} style={{ marginBottom: 24 }}>
            <ToggleCatalogBrowserButton />
            <ViewJobSummaryButton onClick={() => setIsPrintItemsSummaryModalOpen(true)} />
          </Flex>}
        />,
        <VisitsTable
          key="reviewed"
          reviewedState={VisitReviewedState.Reviewed}
          jobId={jobData.id}
          googleApiKey={googleApiKey}
          onEdit={setSelectedVisitId}
        />,
      ],
    })}

    <EditVisitModal
      isOpen={selectedVisitId !== null}
      visitId={selectedVisitId ?? ''}
      jobId={jobData.id}
      onCloseModal={() => setSelectedVisitId(null)}
      googleMapsApiKey={googleApiKey}
      defaultLunchBreakMinutes={currentUser?.tenant.defaultLunchBreak ?? 0}
    />

    <LinkCustomerToQuickBooks
      customerId={linkCustomerId}
      customerDisplayName={jobData.customer.displayName}
      currentQboId={jobData.customer.qboId}
      currentQboDisplayName={jobData.customer.qboDisplayName}
      onSuccess={() => {
        jobQuery.refetch()
        setLinkCustomerId(undefined)
      }}
      onClose={() => setLinkCustomerId(undefined)}
    />

    <LinkCostCodeToQuickBooks
      costCodeId={linkCostCodeId}
      onSuccess={() => setLinkCostCodeId(undefined)}
      onClose={() => setLinkCostCodeId(undefined)}
    />

    <PrintItemsSummaryModal
      open={isPrintItemsSummaryModalOpen}
      onCancel={() => setIsPrintItemsSummaryModalOpen(false)}
      jobId={jobData.id}
      jobType={jobData.type}
    />
  </ItemsRoot>
}

export default JobInvoicingBody
